<template>
  <div class="layout">
    <header class="header">
      <div class="header-left">
        <svg class="hamburger" @click="toggleSidebar" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="3" y1="12" x2="21" y2="12"></line>
          <line x1="3" y1="6" x2="21" y2="6"></line>
          <line x1="3" y1="18" x2="21" y2="18"></line>
        </svg>
        <div class="logo" @click="goHome">
          <svg class="logo-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="2" y1="12" x2="22" y2="12"></line>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
          </svg>
          <span class="logo-text">MetabHub</span>
        </div>
        <div class="logo-meta">
          <span>v0.1-alpha</span>
          <span>Last update: 2026/9/14</span>
        </div>
      </div>
      <h1 class="header-title">Multi-Omics Evidence & Discovery Platform</h1>
      <div class="header-right">
        <div class="tabs">
          <span class="tab">Exposure</span>
          <span class="tab">· Metabolite</span>
          <span class="tab">· Disease</span>
          <span class="tab">· Genetics</span>
        </div>
      </div>
    </header>

    <div class="layout-body">
      <aside class="sidebar" :class="{ collapsed: sidebarCollapsed }">
        <div class="sidebar-menu">
          <div
            v-for="item in menuItems"
            :key="item.name"
            class="menu-group"
          >
            <div
              class="menu-item"
              :class="{ active: isMenuItemActive(item), expandable: item.children }"
              :title="item.description || ''"
              @click="handleMenuItemClick(item)"
            >
              <component :is="item.icon" class="menu-icon" />
              <span>{{ item.name }}</span>
              <svg
                v-if="item.children"
                class="menu-caret"
                :class="{ expanded: associationMenuExpanded }"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
              <div v-if="item.description" class="menu-tooltip">{{ item.description }}</div>
            </div>

            <div
              v-if="item.children && associationMenuExpanded"
              class="submenu"
            >
              <div
                v-for="child in item.children"
                :key="child.name"
                class="submenu-item"
                :class="{ active: currentRoute === child.path || currentRoute.startsWith(`${child.path}/`) }"
                :title="child.description || ''"
                @click="navigateTo(child.path)"
              >
                <component :is="child.icon" class="submenu-icon" />
                <span>{{ child.name }}</span>
                <div v-if="child.description" class="menu-tooltip submenu-tooltip">{{ child.description }}</div>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <main class="content">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script>
const IconExplore = {
  render(h) {
    return h('svg', {
      attrs: {
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2'
      }
    }, [
      h('circle', { attrs: { cx: '12', cy: '5', r: '3' } }),
      h('circle', { attrs: { cx: '5', cy: '12', r: '3' } }),
      h('circle', { attrs: { cx: '19', cy: '12', r: '3' } }),
      h('circle', { attrs: { cx: '8', cy: '19', r: '3' } }),
      h('circle', { attrs: { cx: '16', cy: '19', r: '3' } }),
      h('line', { attrs: { x1: '12', y1: '5', x2: '5', y2: '12' } }),
      h('line', { attrs: { x1: '12', y1: '5', x2: '19', y2: '12' } }),
      h('line', { attrs: { x1: '5', y1: '12', x2: '8', y2: '19' } }),
      h('line', { attrs: { x1: '19', y1: '12', x2: '16', y2: '19' } })
    ])
  }
}

const IconHome = {
  render(h) {
    return h('svg', {
      attrs: {
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2'
      }
    }, [
      h('path', { attrs: { d: 'M3 11 12 4l9 7' } }),
      h('path', { attrs: { d: 'M5 10v10h14V10' } }),
      h('path', { attrs: { d: 'M10 20v-6h4v6' } })
    ])
  }
}

const IconMachineLearning = {
  render(h) {
    return h('svg', {
      attrs: {
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2'
      }
    }, [
      h('rect', { attrs: { x: '3', y: '4', width: '12', height: '16', rx: '2' } }),
      h('line', { attrs: { x1: '7', y1: '8', x2: '11', y2: '8' } }),
      h('line', { attrs: { x1: '7', y1: '12', x2: '11', y2: '12' } }),
      h('circle', { attrs: { cx: '17', cy: '15', r: '4' } }),
      h('line', { attrs: { x1: '17', y1: '13', x2: '17', y2: '17' } }),
      h('line', { attrs: { x1: '15', y1: '15', x2: '19', y2: '15' } })
    ])
  }
}

const IconGlobalSearch = {
  render(h) {
    return h('svg', {
      attrs: {
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2'
      }
    }, [
      h('circle', { attrs: { cx: '11', cy: '11', r: '7' } }),
      h('line', { attrs: { x1: '16.65', y1: '16.65', x2: '21', y2: '21' } }),
      h('path', { attrs: { d: 'M8 11h6' } }),
      h('path', { attrs: { d: 'M11 8v6' } })
    ])
  }
}

const IconMeasurement = {
  render(h) {
    return h('svg', {
      attrs: {
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2'
      }
    }, [
      h('path', { attrs: { d: 'M9 3v10a3 3 0 0 0 6 0V3' } }),
      h('line', { attrs: { x1: '9', y1: '7', x2: '15', y2: '7' } }),
      h('line', { attrs: { x1: '12', y1: '16', x2: '12', y2: '21' } }),
      h('line', { attrs: { x1: '9', y1: '21', x2: '15', y2: '21' } })
    ])
  }
}

const IconGene = {
  render(h) {
    return h('svg', {
      attrs: {
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2'
      }
    }, [
      h('path', { attrs: { d: 'M8 4c4 2 4 6 0 8s-4 6 0 8' } }),
      h('path', { attrs: { d: 'M16 4c-4 2-4 6 0 8s4 6 0 8' } }),
      h('line', { attrs: { x1: '9', y1: '7', x2: '15', y2: '7' } }),
      h('line', { attrs: { x1: '9', y1: '12', x2: '15', y2: '12' } }),
      h('line', { attrs: { x1: '9', y1: '17', x2: '15', y2: '17' } })
    ])
  }
}

const IconExposure = {
  render(h) {
    return h('svg', {
      attrs: {
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2'
      }
    }, [
      h('path', { attrs: { d: 'M12 21v-8' } }),
      h('path', { attrs: { d: 'M7 11c0-4 3-7 5-8 2 1 5 4 5 8a5 5 0 1 1-10 0Z' } }),
      h('path', { attrs: { d: 'M12 13c2-1 3-3 3-5' } })
    ])
  }
}

const IconDisease = {
  render(h) {
    return h('svg', {
      attrs: {
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2'
      }
    }, [
      h('rect', { attrs: { x: '4', y: '14', width: '4', height: '8' } }),
      h('rect', { attrs: { x: '10', y: '8', width: '4', height: '14' } }),
      h('rect', { attrs: { x: '16', y: '4', width: '4', height: '18' } }),
      h('line', { attrs: { x1: '2', y1: '22', x2: '22', y2: '22' } })
    ])
  }
}

const IconSurvival = {
  render(h) {
    return h('svg', {
      attrs: {
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2'
      }
    }, [
      h('polyline', { attrs: { points: '3 18 8 12 13 14 21 4' } }),
      h('polyline', { attrs: { points: '3 22 8 16 13 18 21 8' } }),
      h('line', { attrs: { x1: '2', y1: '22', x2: '22', y2: '22' } })
    ])
  }
}

const IconPrediction = {
  render(h) {
    return h('svg', {
      attrs: {
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2'
      }
    }, [
      h('line', { attrs: { x1: '18', y1: '20', x2: '18', y2: '10' } }),
      h('line', { attrs: { x1: '12', y1: '20', x2: '12', y2: '4' } }),
      h('line', { attrs: { x1: '6', y1: '20', x2: '6', y2: '14' } })
    ])
  }
}

const IconHelp = {
  render(h) {
    return h('svg', {
      attrs: {
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round'
      }
    }, [
      h('circle', { attrs: { cx: '12', cy: '12', r: '10' } }),
      h('path', { attrs: { d: 'M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3' } }),
      h('circle', { attrs: { cx: '12', cy: '17.5', r: '0.9', fill: 'currentColor', stroke: 'none' } })
    ])
  }
}

const IconDownload = {
  render(h) {
    return h('svg', {
      attrs: {
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round'
      }
    }, [
      h('path', { attrs: { d: 'M12 3v12' } }),
      h('path', { attrs: { d: 'm7 10 5 5 5-5' } }),
      h('path', { attrs: { d: 'M5 21h14' } })
    ])
  }
}

export default {
  name: 'Layout',
  components: {
    IconHome,
    IconExplore,
    IconMeasurement,
    IconGene,
    IconExposure,
    IconMachineLearning,
    IconGlobalSearch,
    IconDisease,
    IconSurvival,
    IconPrediction,
    IconHelp,
    IconDownload
  },
  data() {
    return {
      sidebarCollapsed: false,
      associationMenuExpanded: true,
      menuItems: [
        { name: 'Homepage', path: '/', icon: 'IconHome' },
        {
          name: 'Association & Mediations',
          icon: 'IconExplore',
          children: [
            {
              name: 'Clinical Measurements',
              path: '/measurement',
              icon: 'IconMeasurement',
              description: 'Browse disease associations for a selected measurement variable.'
            },
            {
              name: 'Genotype',
              path: '/gene',
              icon: 'IconGene',
              description: 'Browse genes, variants, annotations, and genomic evidence across the multi-omics landscape.'
            },
            {
              name: 'Environment Factors',
              path: '/exposure',
              icon: 'IconExposure',
              description: 'Explore lifestyle, diet, and exposure-related factors associated with health outcomes.'
            }
          ]
        },
        { name: 'Global Search', path: '/global-search', icon: 'IconGlobalSearch' },
        {
          name: 'Disease Risk Prediction',
          path: '/disease-risk-prediction',
          icon: 'IconMachineLearning',
          description: 'Browse machine learning evaluation outputs by disease, sex, and model type.'
        },
        {
          name: 'Disease Risk Stratification',
          path: '/disease-risk-stratification',
          icon: 'IconSurvival',
          description: 'Browse Cox survival summary results by phenotype and sex.'
        },
        {
          name: 'Download',
          path: '/download',
          icon: 'IconDownload',
          description: 'Download complete association and mediation result archives.'
        },
        { name: 'Help & Resources', path: '/about', icon: 'IconHelp' }
      ]
    }
  },
  computed: {
    currentRoute() {
      return this.$route.path
    }
  },
  watch: {
    '$route.path': {
      immediate: true,
      handler(path) {
        if (this.isAssociationRoute(path)) {
          this.associationMenuExpanded = true
        }
      }
    }
  },
  methods: {
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
    },
    goHome() {
      if (this.$route.path !== '/') {
        this.$router.push('/')
      }
    },
    isAssociationRoute(path) {
      return ['/measurement', '/gene', '/exposure'].some(basePath => path === basePath || path.startsWith(`${basePath}/`))
    },
    isMenuItemActive(item) {
      if (item.children) {
        return item.children.some(child => this.currentRoute === child.path || this.currentRoute.startsWith(`${child.path}/`))
      }
      return this.currentRoute === item.path
    },
    handleMenuItemClick(item) {
      if (item.children) {
        this.associationMenuExpanded = !this.associationMenuExpanded
        return
      }
      this.navigateTo(item.path)
    },
    navigateTo(path) {
      if (this.$route.path !== path) {
        const existingRoute = this.$router.options.routes.find(r => r.path === path)
        if (existingRoute) {
          this.$router.push(path)
        }
      }
    }
  }
}
</script>

<style lang="less" scoped>
.layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
}

.layout-body {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.sidebar {
  width: 140px;
  background: #991b1b;
  display: flex;
  flex-direction: column;
  padding: 24px 0;
  flex-shrink: 0;
  transition: width 0.3s ease, opacity 0.3s ease, transform 0.3s ease;
  overflow: visible;

  &.collapsed {
    width: 0;
    padding: 24px 0;
    opacity: 0;
    transform: translateX(-100%);
  }
}

.sidebar-menu {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  min-width: 140px;
  overflow: visible;
}

.menu-group {
  width: 120px;
  overflow: visible;
}

.menu-item {
  width: 120px;
  height: 76px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  position: relative;
  overflow: visible;
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &.expandable {
    height: auto;
    min-height: 76px;
    padding: 12px 8px 10px;
  }

  &.active {
    background: rgba(255, 255, 255, 0.15);

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 32px;
      background: #ffffff;
      border-radius: 0 3px 3px 0;
    }
  }

  span {
    font-size: 14px;
    margin-top: 8px;
    font-weight: 500;
    text-align: center;
    line-height: 1.3;
  }
}

.menu-icon {
  width: 32px;
  height: 32px;
}

.menu-caret {
  width: 16px;
  height: 16px;
  margin-top: 6px;
  transition: transform 0.2s ease;

  &.expanded {
    transform: rotate(180deg);
  }
}

.submenu {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: visible;
}

.submenu-item {
  min-height: 44px;
  border-radius: 8px;
  padding: 8px 10px;
  color: rgba(255, 255, 255, 0.92);
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background 0.2s ease;
  position: relative;
  overflow: visible;
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &.active {
    background: rgba(255, 255, 255, 0.15);
  }

  span {
    font-size: 13px;
    line-height: 1.3;
  }
}

.submenu-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.menu-tooltip {
  position: absolute;
  left: calc(100% + 12px);
  top: 50%;
  transform: translateY(-50%);
  width: 260px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(17, 24, 39, 0.96);
  color: #ffffff;
  font-size: 12px;
  line-height: 1.55;
  text-align: left;
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.22);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.18s ease;
  z-index: 50;
  white-space: normal;
}

.menu-tooltip::before {
  content: '';
  position: absolute;
  left: -6px;
  top: 50%;
  width: 12px;
  height: 12px;
  background: rgba(17, 24, 39, 0.96);
  transform: translateY(-50%) rotate(45deg);
}

.submenu-tooltip {
  left: calc(100% + 10px);
  width: 220px;
}

.menu-item:hover .menu-tooltip,
.submenu-item:hover .menu-tooltip {
  opacity: 1;
}

.header {
  height: 64px;
  width: 100%;
  background: #991b1b;
  display: flex;
  align-items: center;
  padding: 0 24px;
  gap: 24px;
  flex-shrink: 0;
  position: relative;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.hamburger {
  width: 24px;
  height: 24px;
  color: #ffffff;
  cursor: pointer;
}

.logo {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.logo-icon {
  width: 32px;
  height: 32px;
  color: #ffffff;
}

.logo-text {
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.5px;
}

.logo-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 16px;
  line-height: 1.2;
  white-space: nowrap;
}

.header-title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  max-width: calc(100% - 48px);
  text-align: center;
  font-size: 22px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-right {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.tab {
  font-size: 12px;
  color: #ffffff;
  opacity: 0.9;
  white-space: nowrap;
}

.content {
  flex: 1;
  overflow: auto;
  position: relative;
}

@media (max-width: 1100px) {
  .logo-meta {
    gap: 6px;
    font-size: 14px;
  }
}
</style>
